import { CommentOutlined } from '@ant-design/icons'
import { Alert, Button, Input, message, Radio } from 'antd'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useOnboarding } from '../../contexts/OnboardingContext'
import { getApiErrorMessage } from '../../helpers/ApiErrorMessage'
import {
  dismissNpsForSession,
  npsApi,
  type CurrentNpsSurvey,
  type NpsResponseInput,
  wasNpsDismissedForSession
} from '../../helpers/NpsApi'
import * as S from './style'

type RatingKey =
  | 'reportTimeReductionRating'
  | 'regulatoryConfidenceRating'
  | 'easeOfUseRating'
  | 'supportQualityRating'

type Ratings = Record<RatingKey, number | null>

const AUTH_PATH_PREFIXES = ['/login', '/cadastrar', '/cadastre-se', '/criar-senha', '/recuperar-senha', '/onboarding']

const RATING_QUESTIONS: Array<{ key: RatingKey, label: string }> = [
  { key: 'reportTimeReductionRating', label: 'Redução do tempo gasto para fazer relatórios de rotulagem' },
  { key: 'regulatoryConfidenceRating', label: 'Confiança de que os relatórios gerados estão em conformidade com a ANVISA' },
  { key: 'easeOfUseRating', label: 'Facilidade de uso da plataforma' },
  { key: 'supportQualityRating', label: 'Qualidade do suporte e atendimento da equipe Forpy' }
]

const INITIAL_RATINGS: Ratings = {
  reportTimeReductionRating: null,
  regulatoryConfidenceRating: null,
  easeOfUseRating: null,
  supportQualityRating: null
}

function scoreTone (score: number): S.ScoreTone {
  if (score >= 9) return 'promoter'
  if (score >= 7) return 'passive'
  return 'detractor'
}

function resolveUserKey (user: Record<string, unknown> | null): string {
  if (!user) return ''
  const key = user.id ?? user.id_user ?? user.rowid ?? user.email ?? user.username
  return typeof key === 'string' ? key : ''
}

export function NpsSurveyModal (): React.ReactElement | null {
  const { pathname } = useLocation()
  const { user, userCan } = useAuth()
  const { state: onboarding, isLoading: isLoadingOnboarding } = useOnboarding()
  const [survey, setSurvey] = useState<CurrentNpsSurvey | null>(null)
  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [score, setScore] = useState<number | null>(null)
  const [ratings, setRatings] = useState<Ratings>(INITIAL_RATINGS)
  const [scoreReason, setScoreReason] = useState('')
  const [improvementSuggestion, setImprovementSuggestion] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const checkedSessionRef = useRef<string | null>(null)
  const requestIdRef = useRef(0)
  const [messageApi, messageContextHolder] = message.useMessage()

  const userKey = resolveUserKey(user)
  const isAdmin = userCan('admin:all')
  const isAuthPage = isLoadingOnboarding || Boolean(onboarding?.step) || AUTH_PATH_PREFIXES.some(prefix => pathname.startsWith(prefix))
  const hasRequiredAnswers = score !== null && Object.values(ratings).every(value => value !== null)

  const handleRatingChange = useCallback((key: RatingKey, value: number) => {
    setRatings(current => ({ ...current, [key]: value }))
    setErrorMessage(null)
  }, [])

  const handleDismiss = useCallback(() => {
    if (survey && userKey) dismissNpsForSession(survey.version, userKey)
    setOpen(false)
  }, [survey, userKey])

  const handleSubmit = useCallback(async () => {
    const {
      reportTimeReductionRating,
      regulatoryConfidenceRating,
      easeOfUseRating,
      supportQualityRating
    } = ratings
    if (
      score === null ||
      reportTimeReductionRating === null ||
      regulatoryConfidenceRating === null ||
      easeOfUseRating === null ||
      supportQualityRating === null
    ) {
      setErrorMessage('Responda a nota principal e as quatro avaliações antes de enviar.')
      return
    }

    const payload: NpsResponseInput = {
      score,
      reportTimeReductionRating,
      regulatoryConfidenceRating,
      easeOfUseRating,
      supportQualityRating,
      ...(scoreReason.trim() ? { scoreReason: scoreReason.trim() } : {}),
      ...(improvementSuggestion.trim() ? { improvementSuggestion: improvementSuggestion.trim() } : {})
    }

    setSubmitting(true)
    setErrorMessage(null)
    try {
      await npsApi.submit(payload)
      setOpen(false)
      messageApi.success('Obrigada por contribuir com a evolução da Forpy!')
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, 'Não foi possível enviar sua avaliação. Tente novamente.'))
    } finally {
      setSubmitting(false)
    }
  }, [improvementSuggestion, messageApi, ratings, score, scoreReason])

  useEffect(() => {
    const sessionToken = window.localStorage.getItem('sessionToken')
    if (!sessionToken || !userKey || isAdmin || isAuthPage) {
      requestIdRef.current += 1
      checkedSessionRef.current = null
      setSurvey(null)
      setOpen(false)
      return
    }

    const checkKey = `${userKey}:${sessionToken}`
    if (checkedSessionRef.current === checkKey) return
    checkedSessionRef.current = checkKey
    const requestId = ++requestIdRef.current
    setScore(null)
    setRatings(INITIAL_RATINGS)
    setScoreReason('')
    setImprovementSuggestion('')
    setErrorMessage(null)

    void npsApi.getCurrent()
      .then(currentSurvey => {
        if (requestId !== requestIdRef.current) return
        setSurvey(currentSurvey)
        const shouldOpen = currentSurvey.eligible &&
          !currentSurvey.answered &&
          !wasNpsDismissedForSession(currentSurvey.version, userKey)
        setOpen(shouldOpen)
      })
      .catch(error => {
        if (requestId !== requestIdRef.current) return
        checkedSessionRef.current = null
        console.error('Erro ao verificar NPS pendente:', error)
      })
  }, [isAdmin, isAuthPage, userKey])

  if (!userKey || isAdmin) return null

  return (
    <>
      {messageContextHolder}
      <S.SurveyModal
        open={open}
        title='Queremos ouvir você'
        width={720}
        zIndex={11000}
        centered
        closable={!submitting}
        maskClosable={!submitting}
        keyboard={!submitting}
        destroyOnHidden
        onCancel={handleDismiss}
        footer={[
          <Button key='later' disabled={submitting} onClick={handleDismiss}>
            Agora não
          </Button>,
          <Button
            key='submit'
            type='primary'
            loading={submitting}
            disabled={!hasRequiredAnswers}
            onClick={() => { void handleSubmit() }}
          >
            Enviar avaliação
          </Button>
        ]}
      >
        <S.Content>
          <S.Intro>
            <span className='intro-icon' aria-hidden='true'>
              <CommentOutlined />
            </span>
            <div>
              <strong>Sua experiência ajuda a construir uma Forpy melhor</strong>
              <p>A avaliação leva poucos minutos e será enviada uma única vez nesta versão.</p>
            </div>
          </S.Intro>

          {errorMessage && <Alert type='error' showIcon message={errorMessage} />}

          <S.Section>
            <header>
              <h3>Em uma escala de 0 a 10, o quanto você recomendaria a plataforma de rotulagem da Forpy para um colega ou outra empresa do setor de alimentos?</h3>
            </header>
            <S.ScoreGrid role='radiogroup' aria-label='Nota de recomendação da Forpy'>
              {Array.from({ length: 11 }, (_, value) => (
                <S.ScoreButton
                  key={value}
                  type='button'
                  role='radio'
                  aria-checked={score === value}
                  aria-label={`Nota ${value}`}
                  $selected={score === value}
                  $tone={scoreTone(value)}
                  onClick={() => {
                    setScore(value)
                    setErrorMessage(null)
                  }}
                >
                  {value}
                </S.ScoreButton>
              ))}
            </S.ScoreGrid>
            <S.ScaleLabels aria-hidden='true'>
              <span>0 = Não recomendaria de forma alguma</span>
              <span>10 = Recomendaria com certeza</span>
            </S.ScaleLabels>
          </S.Section>

          <S.Section>
            <header>
              <h3>Avalie alguns pontos específicos</h3>
              <p>De 1 (muito insatisfeito) a 5 (muito satisfeito). Todas as avaliações são obrigatórias.</p>
            </header>
            <S.RatingsPanel>
              {RATING_QUESTIONS.map(question => (
                <S.RatingRow key={question.key}>
                  <span id={`nps-${question.key}`}>{question.label}</span>
                  <Radio.Group
                    value={ratings[question.key]}
                    aria-labelledby={`nps-${question.key}`}
                    onChange={event => handleRatingChange(question.key, Number(event.target.value))}
                  >
                    {[1, 2, 3, 4, 5].map(value => (
                      <Radio.Button key={value} value={value}>{value}</Radio.Button>
                    ))}
                  </Radio.Group>
                </S.RatingRow>
              ))}
            </S.RatingsPanel>
          </S.Section>

          <S.Section>
            <header>
              <h3>Se quiser, conte um pouco mais</h3>
              <p>Os campos abaixo são opcionais.</p>
            </header>
            <S.TextFields>
              <S.TextField>
                <span>Qual o principal motivo da sua nota?</span>
                <Input.TextArea
                  value={scoreReason}
                  maxLength={1000}
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  placeholder='Conte o que mais influenciou sua avaliação'
                  onChange={event => setScoreReason(event.target.value)}
                />
              </S.TextField>
              <S.TextField>
                <span>Há algo que a Forpy poderia fazer para tornar sua rotina de rotulagem ainda melhor?</span>
                <Input.TextArea
                  value={improvementSuggestion}
                  maxLength={1000}
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  placeholder='Compartilhe uma sugestão, se desejar'
                  onChange={event => setImprovementSuggestion(event.target.value)}
                />
              </S.TextField>
            </S.TextFields>
          </S.Section>

          <S.Thanks>Obrigada por contribuir com a evolução da Forpy!</S.Thanks>
        </S.Content>
      </S.SurveyModal>
    </>
  )
}
